/* global Blockly */

import React from 'react';
import PropTypes from 'prop-types';
import MobiControlCore from '../../../v1/blocks/MobiControl/MobiControlCore';
import util from '../../../../../../utils/es5Utils'

function defineBlock(context) {
    let prop = {...context};
    prop.tooltipText = "Execute a MobiControl action (e.g. Login, Logout, Launch, Surf, Custom) with an optional action-specific value. The Execute MobiControl Action JS API is available within lockdown apps.";
    prop.responseText = "result";
    prop.responseBlockType = "text_response";
    let blockInstance = MobiControlCore(prop);

    Object.assign(blockInstance, {
        initialized: false,
        isMoving: false,

        // the 16 supported MC actions (Snap.mcLockdown.constants.mcActions.<name>)
        mcActions: [
            [' Login  ', 'Login'],
            [' Logout  ', 'Logout'],
            [' Launch  ', 'Launch'],
            [' LaunchAlways  ', 'LaunchAlways'],
            [' LaunchWithRecents  ', 'LaunchWithRecents'],
            [' Surf  ', 'Surf'],
            [' Intent  ', 'Intent'],
            [' Dial  ', 'Dial'],
            [' Browser  ', 'Browser'],
            [' Browsers  ', 'Browsers'],
            [' File  ', 'File'],
            [' Http  ', 'Http'],
            [' Https  ', 'Https'],
            [' Action  ', 'Action'],
            [' Movie  ', 'Movie'],
            [' Custom  ', 'Custom']
        ],

        // override default fields
        addFields: function() {
            this.appendValueInput("ACTION")
                .appendField("execute MC action")
                .setCheck(['String', 'dropdown_options']);
            this.appendValueInput("VALUE")
                .appendField("with value")
                .setCheck("String");
        },

        // change handler - keeps the ACTION dropdown always connected/coloured
        customChangeHandler: function(event) {
            let actionInput = this.getInput('ACTION');
            if (actionInput.connection.isConnected()) {
                let actionTargetBlock = actionInput.connection.targetBlock();
                if (actionTargetBlock.type === 'dropdown_options' && actionTargetBlock.customColor !== Blockly.Msg.MOBICONTROL_HUE) {
                    // when rendered for the first time, the color is reset for existing block - refresh it
                    actionTargetBlock.setColour(Blockly.Msg.MOBICONTROL_HUE);
                    actionTargetBlock.customColor = Blockly.Msg.MOBICONTROL_HUE;
                }
            } else {
                // auto-create drop-down block with the predefined actions
                let ddActionBlock = this.workspace.newBlock('dropdown_options');
                ddActionBlock.setFieldDropdown(this.mcActions, undefined, undefined);
                ddActionBlock.setColour(Blockly.Msg.MOBICONTROL_HUE);
                ddActionBlock.customColor = Blockly.Msg.MOBICONTROL_HUE;
                ddActionBlock.setShadow(true);
                if (this.rendered) {
                    ddActionBlock.initSvg();
                    ddActionBlock.render();
                }
                actionInput.connection.connect(ddActionBlock.outputConnection);
            }
            this.initialized = true;

            if (event.type === Blockly.Events.MOVE/* there is no OnDragStarted event */ && !this.isMoving) {
                this.getChildren(false).forEach((childBlock) => {
                    childBlock.render();
                });
                this.isMoving = true;
                this.unselect(); // block appears to be selected while dragging - fixed
            }
            if (event.type === Blockly.Events.END_DRAG && this.isMoving) {
                this.getChildren(false).forEach((childBlock) => {
                    childBlock.render();
                });
                this.isMoving = false;
            }
        }
    });
    return blockInstance;
}

function defineGenerators() {
    return {
        'JavaScript': function (block) {
            let coreResponseCode = block.getJavascriptCore();

            let action = Blockly.JavaScript.valueToCode(block, "ACTION", Blockly.JavaScript.ORDER_NONE);
            let actionBlock = block.getInput("ACTION");
            if (!(actionBlock?.connection?.isConnected() && actionBlock.connection.targetConnection?.getSourceBlock().type === "dropdown_options")) {
                action = util.parseString(action);
            }

            // warn (but still generate safely) when a manually-typed ACTION isn't one of the known mcActions
            let isKnownAction = block.mcActions.some(([, actionName]) => actionName === action);
            block.setWarningText(isKnownAction ? null : `"${action}" is not a recognized MC action.`);

            // customUri: null when left blank (matches MobiControl's own sample scripts). A literal "text" block's
            // generated code is already safely-escaped by Blockly itself, so it's embedded as-is; anything else
            // (Text Join, a variable, etc.) is a runtime expression and must be evaluated via String(...) instead.
            let rawValueCode = Blockly.JavaScript.valueToCode(block, "VALUE", Blockly.JavaScript.ORDER_NONE);
            let valueBlock = block.getInput("VALUE");
            let valueTargetBlock = valueBlock?.connection?.isConnected() ? valueBlock.connection.targetConnection?.getSourceBlock() : null;
            let customUri;
            if (!valueTargetBlock || valueTargetBlock.type === "text") {
                let isBlank = !valueTargetBlock || valueTargetBlock.getFieldValue('TEXT') === '';
                customUri = isBlank ? 'null' : rawValueCode;
            } else {
                customUri = `String(${rawValueCode})`;
            }

            // bracket notation with a JSON-stringified key keeps this safe even if action is unrecognized/malicious -
            // it can only ever resolve to a property lookup, never splice arbitrary code into the generated script
            return `Snap.mcLockdown.executeMCAction(Snap.mcLockdown.constants.mcActions[${JSON.stringify(action)}], ${customUri}, ${coreResponseCode});\n`;
        }
    }
}

const MobiControlExecuteMCAction = function (props) {
    const name = 'snap_mobicontrol_executemcaction';
    MobiControlExecuteMCAction.blockName = name;

    Blockly.Blocks[name] = defineBlock(props.context);
    let generators = defineGenerators();
    for (let i in generators) {
        Blockly[i][name] = generators[i];
    }

    return  <block type={name}>
                <value name="VALUE">
                    <shadow type="text"></shadow>
                </value>
            </block>;
}

MobiControlExecuteMCAction.propTypes = {
    context: PropTypes.object
};

export default MobiControlExecuteMCAction;
